import { Component } from "@theme/component";
import { ThemeEvents, VariantUpdateEvent, QuantitySelectorUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

/**
 * A custom element that manages a sticky add to cart bar.
 * Syncs variant selection and quantity with the main variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class StickyAddToCart extends Component {
  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  /** @type {IntersectionObserver | undefined} */
  #footerObserver;

  /** @type {MutationObserver | undefined} */
  #mutationObserver;

  /** @type {HTMLElement | null} */
  #footerSentinel = null;

  /** @type {boolean} */
  #isStuck = false;

  /**
   * Whether the footer is within its approach margin (rootMargin) of the
   * viewport — drives the "hide near footer" avoidance rule.
   * @type {boolean}
   */
  #footerNear = false;

  /** @type {boolean} */
  #footerEditing = false;

  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {number | null} */
  #checkTimeout = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const target = this.closest('.shopify-section');
    if (target) {
      this.boundHandleVariantUpdate = this.#handleVariantUpdate.bind(this);
      target.addEventListener(ThemeEvents.variantUpdate, this.boundHandleVariantUpdate);
    }
    this.boundHandleQuantityUpdate = this.#handleQuantityUpdate.bind(this);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.boundHandleQuantityUpdate);

    // In the theme editor, lock the sticky bar hidden while the footer section
    // is being edited so it never overlaps the footer.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
    }

    // Setup ResizeObserver to track height changes
    if (window.ResizeObserver) {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#setHeight(this.clientHeight);
      });
      this.#resizeObserver.observe(this);
    }

    // Set initial height immediately to avoid delay
    this.#setHeight(this.clientHeight);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Cleanup timeout
    if (this.#checkTimeout) {
      clearTimeout(this.#checkTimeout);
      this.#checkTimeout = null;
    }

    if (this.boundHandleVariantUpdate) {
      const target = this.closest('.shopify-section');
      target?.removeEventListener(ThemeEvents.variantUpdate, this.boundHandleVariantUpdate);
    }
    if (this.boundHandleQuantityUpdate) {
      document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.boundHandleQuantityUpdate);
    }

    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);

    this.#intersectionObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#resizeObserver?.unobserve(this);

    this.#footerSentinel?.remove();
    this.#footerSentinel = null;
  }

  /**
   * Sets CSS custom property with element height
   * @param {number} height - The height value in pixels
   */
  #setHeight(height) {
    document.documentElement.style.setProperty('--sticky-add-to-cart-height', `${height}px`);
  }

  /**
   * Sets up intersection observer to show/hide sticky bar when product-form-component is scrolled past
   */
  #setupIntersectionObserver() {
    const mainProductForm = this.#getProductForm();
    if (!mainProductForm) return;

    this.#setupFormObserver();
    this.setupFooterObserver();
    this.#setupMutationObserver();
  }

  /**
   * Sets up form observer to show/hide sticky bar when product form enters or leaves the viewport
   */
  #setupFormObserver() {
    const mainProductForm = this.#getProductForm();
    if (!mainProductForm) return;
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        // Single visibility rule (see #updateVisibility): the bar follows the
        // form whether it is still below the viewport or already scrolled past.
        this.#updateVisibility(entry.target);
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0,
      }
    );

    this.#intersectionObserver.observe(mainProductForm);
  }


  /**
   * Sets up footer observer to hide sticky bar when footer is visible
   */
  setupFooterObserver() {
    const mainProductForm = this.#getProductForm();
    if (!mainProductForm) return;

    const footerTarget = this.#getFooterObserverTarget();
    if (!footerTarget) return;

    // Avoid stacking observers when re-run (e.g. theme editor re-renders)
    this.#footerObserver?.disconnect();

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        this.#footerNear = entry.isIntersecting;
        if (!this.#footerNear) {
          // Footer left the approach zone — release the editor lock so the bar
          // can reappear as soon as the user scrolls back over the product.
          this.#footerEditing = false;
        }
        // Same unified rule as the form observer, so both callbacks can never
        // disagree about visibility.
        this.#updateVisibility(mainProductForm);
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#footerObserver.observe(footerTarget);
  }

  /**
   * Sets up MutationObserver to detect DOM changes (e.g., after AJAX loads content)
   */
  #setupMutationObserver() {
    const mainProductForm = this.#getProductForm();
    if (!mainProductForm) return;
    const main = document.querySelector('main');
    if (!main) return;

    let checkTimeout;

    this.#mutationObserver = new MutationObserver(() => {
      // Debounce the check to avoid excessive calls
      if (this.#checkTimeout) {
        clearTimeout(this.#checkTimeout);
      }
      this.#checkTimeout = setTimeout(() => {
        requestAnimationFrame(() => {
          this.#updateVisibility(mainProductForm);
        });
        this.#checkTimeout = null;
      }, 100);
    });

    // Observe changes to main content
    this.#mutationObserver.observe(main, {
      childList: true,
      subtree: true,
    });
    }

  /**
   * Unified visibility rule, shared by every trigger (form observer, footer
   * observer, DOM mutations, editor events): show the sticky bar whenever the
   * main product form is not visible in the viewport — whether it is still
   * below the fold or already scrolled past — and the footer is not near.
   * Hide it as soon as either one is visible, so the bar never doubles the
   * main buy module or overlaps the footer.
   * @param {HTMLElement | null} mainProductForm - The main product form element
   */
  #updateVisibility(mainProductForm) {
    if (!mainProductForm) return;

    const rect = mainProductForm.getBoundingClientRect();
    const formVisible = rect.top < window.innerHeight && rect.bottom > 0;

    if (!formVisible && !this.#footerNear) {
      this.#showStickyBar();
    } else {
      this.hideStickyBar();
    }
  }

  #getFooterObserverTarget() {
    const footerGroup = document.querySelector('.shopify-section-group-footer-group:last-child');
    const lastFooter = footerGroup?.querySelector('footer');

    if (!lastFooter) return null;

    if (!lastFooter.matches('[is="footer-effect"]')) {
      this.#footerSentinel?.remove();
      this.#footerSentinel = null;
      return lastFooter;
    }

    if (this.#footerSentinel?.isConnected === false) {
      this.#footerSentinel = null;
    }

    if (!this.#footerSentinel) {
      this.#footerSentinel = document.createElement('div');
      this.#footerSentinel.className = 'footer-effect-sentinel no-empty';
      this.#footerSentinel.setAttribute('aria-hidden', 'true');
      footerGroup.parentNode?.insertBefore(this.#footerSentinel, footerGroup);
    }

    return this.#footerSentinel;
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
   #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
      }
  };

  /**
   * Shows the sticky bar
   */
  #showStickyBar() {
    // Keep hidden while the footer is being edited in the theme editor
    if (this.#footerEditing) return;
    this.#isStuck = true;
    this.classList.add("is-visible");
  }

  /**
   * Theme editor: footer section selected — lock the sticky bar hidden.
   * @param {Event} event
   */
  #handleSectionSelect = (event) => {
    if (!this.#targetContainsFooter(event.target)) return;
    this.#footerEditing = true;
    this.hideStickyBar();
  };

  /**
   * Theme editor: footer section deselected — release the lock and
   * re-evaluate visibility based on the current scroll position.
   * @param {Event} event
   */
  #handleSectionDeselect = (event) => {
    if (!this.#targetContainsFooter(event.target)) return;
    this.#footerEditing = false;
    this.setupFooterObserver();

    const mainProductForm = this.#getProductForm();
    if (mainProductForm) this.#updateVisibility(mainProductForm);
  };

  /**
   * Whether the selected editor section is (or contains) a footer.
   * Checks the target itself so it works regardless of how many footer
   * sections the page has.
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  #targetContainsFooter(target) {
    if (!(target instanceof Element)) return false;
    return target.matches('footer') || Boolean(target.querySelector('footer'));
  }

  /**
   * Hides the sticky bar
   */
  hideStickyBar() {
    this.#isStuck = false;
    this.classList.remove("is-visible");
  }

    // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    event.preventDefault();
    event.stopPropagation();
    const variantId = event.target.value;
    if (!variantId) return;
    const productFormComponent = this.querySelector('product-form-component');
    if (!productFormComponent) return;

    const variantIdInput = productFormComponent.refs?.variantId;
    if (variantIdInput) {
      variantIdInput.value = variantId;
    }
    const productUrl = event.target.dataset?.productUrl?.split('?')[0];
    if (!productUrl) return;

    this.fetchUpdatedSection(this.buildRequestUrl(productUrl, variantId), variantId);

    const url = new URL(window.location.href);

    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
          }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
      }
    }

  /**
   * Builds request URL for fetching updated variant section
   * Handles combined listings where products may have different URLs
   * @param {string} productUrl - The product URL
   * @param {HTMLElement} selectedOption - The selected variant option
   * @returns {string} The request URL with section_id and option_values
   */
  buildRequestUrl(productUrl, variantId) {
    const params = [];

    if (variantId) {
      params.push(`variant=${variantId}`);
    }

    if (this.getSectionId()) {
      // Remove existing query params before adding new ones
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${this.getSectionId()}&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }


  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string | null} [variantId] - The variant the request was built for.
   */
  fetchUpdatedSection(requestUrl, variantId = null) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    fetch(requestUrl, { signal: this.#fetchController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        let html = new DOMParser().parseFromString(responseText, 'text/html');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;
        if (variantId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), variantId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct: null,
              isStickyAddToCart: true,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Handles quantity selector update events
   * Syncs quantity between main form and sticky bar quantity selectors
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    // Get the quantity selector component that triggered the event
    const sourceQuantitySelector = event.target.closest('quantity-selector-component');
    if (!sourceQuantitySelector) return;

    // Find the section to get both quantity selectors
    const section = this.closest('.shopify-section');
    if (!section) return;

    // Find all quantity selector components in the section
    const allQuantitySelectors = section.querySelectorAll('quantity-selector-component');

    // Find the other quantity selector (not the one that triggered the event)
    const otherQuantitySelector = Array.from(allQuantitySelectors).find(
      (selector) => selector !== sourceQuantitySelector
    );

    if (!otherQuantitySelector) return;

    // Get the input from the other quantity selector
    const otherInput = otherQuantitySelector.querySelector('input[name="quantity"]');
    if (!otherInput) return;

    // Update the other input's value
    otherInput.value = event.detail.quantity;
  };

  /**
   * Gets the section ID
   * @returns {string} The section ID
   */
  getSectionId() {
    return this.getAttribute('data-section-id');
    }

}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCart);
}